import { useState, useEffect } from 'react';
import { FaCircleArrowLeft } from "react-icons/fa6";
//import { HiOutlineBanknotes } from "react-icons/hi2";
import { FaRegCreditCard } from "react-icons/fa";
//import { MdOutlineSummarize } from "react-icons/md";
import { TfiLayoutListThumbAlt } from "react-icons/tfi";
//import { RiCouponFill } from "react-icons/ri";
import { PiTicketFill } from "react-icons/pi";
import { IoPricetags } from "react-icons/io5";
import { FaShippingFast } from "react-icons/fa";
//import { PiMapPinAreaFill } from "react-icons/pi";
import { FaMapPin } from "react-icons/fa";
//import { TbTruckDelivery } from "react-icons/tb";
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useCartStore } from '@/store/cartStore';
import { useAuthStore } from '@/store/authStore';
import { useOrderStore } from '@/store/orderStore';
import { useCouponStore } from '@/store/couponStore';
import { useDeliverySettingsStore } from '@/store/deliverySettingsStore';
import { useResilientOrder } from '@/hooks/useResilientOrder';
import { useResilientAction } from '@/hooks/useResilientAction';
import { generateOrderId } from '@/utils/helpers';
import { supabase } from '@/utils/supabase';
import type { Voucher } from '@/utils/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { BANGLADESH_CITIES, CITY_AREAS, getLocationName } from '@/data/bangladesh-locations';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { paymentService } from '@/utils/payment';
import {
  //Landmark,
  //Truck,
  Check,
  Loader2,
  //MapPin,
  User,
  Tag,
  Phone,
  Search,
  ChevronDown,
  Heart,
} from 'lucide-react';

const BkashIcon = ({ className }: { className?: string }) => (
  <img src="/icons/BKash-bKash-Logo.png" alt="bKash" className={className} style={{ objectFit: 'contain' }} />
);

const NagadIcon = ({ className }: { className?: string }) => (
  <img src="/icons/Nagad-Horizontal-Logo.png" alt="Nagad" className={className} style={{ objectFit: 'contain' }} />
);

const CodIcon = ({ className }: { className?: string }) => (
  <img src="/icons/COD-1.png" alt="Cash on Delivery" className={className} style={{ objectFit: 'contain' }} />
);


export function Checkout() {
  const { t, currentLang } = useI18n();
  const navigate = useNavigate();
  const { addCreatedOrder } = useOrderStore();
  const { items, getTotalPrice, getDeliveryFee, getFinalTotal, clearCart } = useCartStore();
  const { user, profile } = useAuthStore();
  const deliveryConfig = useDeliverySettingsStore((state) => state.config);

  // Zero-Ping Resilience: hook handles RPC + retry, store handles UI state
  const { isLoading, submitOrder, warmConnection } = useResilientOrder();
  const [showSuccess, setShowSuccess] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [createdOrderId, setCreatedOrderId] = useState<string>('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<Voucher | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [cityOpen, setCityOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const { coupons, fetchCoupons, validateCoupon } = useCouponStore();
  const [showCoupons, setShowCoupons] = useState(false);

  const { isLoading: isApplyingCoupon, execute: resilientValidateCoupon } = useResilientAction(
    async (signal: AbortSignal, code: string, amount: number) => {
      return await validateCoupon(code, amount, signal);
    }
  );


  const defaultAddress = Array.isArray(profile?.address) 
    ? (profile.address as NonNullable<typeof profile.address>).find(a => a.is_default) || profile.address[0]
    : profile?.address;

  const [formData, setFormData] = useState({
    fullName: profile?.full_name || '',
    phone: profile?.phone || '',
    street: defaultAddress?.street || '',
    area: defaultAddress?.area || '',
    city: defaultAddress?.city || '',
    notes: '',
  });
  const [citySearch, setCitySearch] = useState('');
  const [areaSearch, setAreaSearch] = useState('');

  useEffect(() => {
    if (appliedCoupon) {
      const currentSubtotal = getTotalPrice();
      if (currentSubtotal < appliedCoupon.min_order_amount) {
        setAppliedCoupon(null);
        setCouponCode('');
        
        const diff = appliedCoupon.min_order_amount - currentSubtotal;
        const msg = currentLang === 'bn'
          ? `ন্যূনতম ৳${appliedCoupon.min_order_amount} অর্ডার প্রয়োজন। ডিসকাউন্ট উপভোগ করতে আরও ৳${diff.toFixed(2)} মূল্যের পণ্য যোগ করুন।`
          : `Minimum order amount ৳${appliedCoupon.min_order_amount} required. Add items worth ৳${diff.toFixed(2)} more to use this coupon.`;
        
        setCouponError(msg);
        toast.error(currentLang === 'bn' ? 'অর্ডার ন্যূনতম মূল্যের নিচে থাকায় কুপন বাতিল করা হয়েছে।' : 'Coupon removed because order subtotal dropped below minimum.');
      }
    }
  }, [items, appliedCoupon, currentLang, getTotalPrice]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyCoupon = async (codeOverride?: string) => {
    const trimmedCode = (typeof codeOverride === 'string' ? codeOverride : couponCode).trim();
    if (!trimmedCode) return;
    setCouponError(null);
    const subtotal = getTotalPrice();
    
    try {
      const result = await resilientValidateCoupon(trimmedCode, subtotal);
      if (!result) return;
      
      const { coupon, error, minAmount } = result;

      if (error) {
        if (error === 'MIN_AMOUNT' && minAmount) {
           const diff = minAmount - subtotal;
           setCouponError(
             currentLang === 'bn' 
               ? `ন্যূনতম ৳${minAmount} অর্ডার প্রয়োজন। ডিসকাউন্ট উপভোগ করতে আরও ৳${diff.toFixed(2)} মূল্যের পণ্য যোগ করুন।`
               : `Minimum order amount ৳${minAmount} required. Add items worth ৳${diff.toFixed(2)} more to use this coupon.`
           );
        } else if (error === 'Invalid coupon code') {
           setCouponError(currentLang === 'bn' ? 'অবৈধ কুপন কোড' : error);
        } else {
           setCouponError(error);
        }
        setAppliedCoupon(null);
      } else {
        setAppliedCoupon(coupon);
        setCouponError(null);
        toast.success(currentLang === 'bn' ? 'কুপন প্রয়োগ করা হয়েছে!' : 'Coupon applied successfully!');
      }
    } catch (err: unknown) {
      setCouponError(err instanceof Error ? err.message : 'Failed to apply coupon');
      setAppliedCoupon(null);
    }
  };

  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    const subtotal = getTotalPrice();
    let discount = 0;
    if (appliedCoupon.discount_type === 'percentage') {
      discount = (subtotal * appliedCoupon.discount_value) / 100;
      if (appliedCoupon.max_discount_amount && discount > appliedCoupon.max_discount_amount) {
        discount = appliedCoupon.max_discount_amount;
      }
    } else {
      discount = appliedCoupon.discount_value;
    }
    // Cap discount at subtotal to prevent negative totals
    return Math.min(discount, subtotal);
  };

  const finalTotal = getFinalTotal() - calculateDiscount();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return; // Prevent double submission (hook guards this too)
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      // Re-validate stock before creating order
      const { data: stockCheck, error: stockError } = await supabase
        .from('products')
        .select('id, name_en, stock_quantity')
        .in('id', items.map(i => i.productId));
      
      if (!stockError && stockCheck) {
        const outOfStock = stockCheck.find(p => {
          const item = items.find(i => i.productId === p.id);
          return item && p.stock_quantity < item.quantity;
        });
        if (outOfStock) {
          throw new Error(`STOCK_ERROR|Insufficient stock for ${outOfStock.name_en}. Only ${outOfStock.stock_quantity} left.`);
        }
      }

      const orderId = generateOrderId();
      const discount = calculateDiscount();

      // Build the order data object (used by both hook and store)
      const orderData = {
        id: orderId,
        user_id: user.id,
        status: 'pending' as const,
        payment_status: 'pending' as const,
        payment_method: paymentMethod as 'cod' | 'bkash' | 'nagad',
        subtotal: getTotalPrice(),
        delivery_fee: getDeliveryFee(),
        discount: discount,
        voucher_id: appliedCoupon?.id || null,
        total: finalTotal,
        delivery_address: {
          street: formData.street,
          area: formData.area,
          city: formData.city
        },
        contact_phone: formData.phone,
        notes: formData.notes || null,
        order_items: items.map(item => ({
          product_id: item.productId,
          quantity: item.quantity,
          price_at_time: item.price,
          total: item.price * item.quantity,
        })),
      };

      // STEP 1: Resilient RPC call (with auto-retry + AbortController timeout).
      // This ONLY hits the database. Returns the server-generated order ID.
      const serverOrderId = await submitOrder(orderData);

      if (serverOrderId) {
        // STEP 2: Update local Zustand state + fire Ably events + staff assignment.
        // This does NOT call the database, preventing double-charge.
        addCreatedOrder(orderData, serverOrderId);

        setCreatedOrderId(serverOrderId);
        
        // For online payments (bKash/Nagad), initiate payment and redirect
        if (paymentMethod === 'bkash' || paymentMethod === 'nagad') {
          toast.info(currentLang === 'bn' ? 'পেমেন্ট গেটওয়েতে রিডাইরেক্ট করা হচ্ছে...' : 'Redirecting to payment gateway...', { duration: 4000 });
          
          const callbackUrl = `${window.location.origin}/payment/callback?gateway=${paymentMethod}&order_id=${serverOrderId}`;
          
          try {
            const initResult = await paymentService.initiatePayment(paymentMethod as 'bkash'|'nagad', finalTotal, serverOrderId, callbackUrl);
            if (initResult.success && initResult.gatewayUrl) {
              window.location.href = initResult.gatewayUrl;
            } else {
              toast.error(initResult.error || 'Payment initiation failed.');
              navigate(`/orders/${serverOrderId}/track`, { replace: true });
            }
          } catch(err: unknown) {
            toast.error(err instanceof Error ? err.message : 'Payment initiation error');
            navigate(`/orders/${serverOrderId}/track`, { replace: true });
          }
        } else {
          // COD: show normal order success immediately.
          setShowSuccess(true);
        }
        // [STABILITY FIX]: Do NOT clear cart here. 
        // If we clear it now, items.length becomes 0 and the useEffect 
        // redirects the user to /cart before the success dialog can even show.
        // We will clear it in handleSuccessClose() instead.
      } else {
        toast.error(t('success.order_error') || 'Order creation failed. Please check your connection or try again.');
      }
    } catch (error) {
      console.error('Order creation failed:', error);
      const msg = error instanceof Error ? error.message : 'Order creation failed. Please try again.';
      
      // Dynamic Throttling & Timeout Handler
      if (msg.includes('THROTTLING|')) {
        const seconds = msg.split('|')[1]?.split(' ')[0] || '30';
        toast.error(
          currentLang === 'bn' 
            ? `অনুগ্রহ করে ${seconds} সেকেন্ড অপেক্ষা করুন। আপনি খুব দ্রুত অর্ডার দিচ্ছেন।`
            : `Please wait ${seconds} seconds. You are placing orders too quickly.`,
          {
            description: currentLang === 'bn' ? 'অ্যান্টি-স্প্যাম সুরক্ষা সক্রিয়' : 'Anti-spam protection active',
            duration: 4000,
          }
        );
      } else if (msg.includes('TIMEOUT|')) {
        toast.error(
          currentLang === 'bn' 
            ? 'অর্ডার প্রসেসিং সময় শেষ হয়ে গেছে।' 
            : 'Order placement timed out.',
          {
            description: currentLang === 'bn' 
              ? 'আপনার ইন্টারনেট সংযোগ পরীক্ষা করে আবার চেষ্টা করুন।' 
              : 'Please check your internet and try again. Refresh if it persists.',
            duration: 6000,
          }
        );
      } else if (msg.includes('STOCK_ERROR|')) {
        const errorDetail = msg.split('|')[1];
        toast.error(errorDetail, { duration: 6000 });
      } else {
        toast.error(msg);
      }
    }
    // NOTE: No finally { setIsLoading(false) } needed here.
    // The useResilientOrder hook manages isLoading internally via its own
    // finally block, guaranteeing the spinner always stops.
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    clearCart(); // Now it is safe to clear the cart!
    
    if (createdOrderId) {
      navigate(`/orders/${createdOrderId}/track`, { replace: true });
    } else {
      navigate('/orders', { replace: true });
    }
  };

  if (items.length === 0 && !showSuccess) {
    navigate('/cart');
    return null;
  }

  const paymentMethods = [
    {
      id: 'cod',
      label: t('checkout.cod'),
      icon: CodIcon,
      description: currentLang === 'bn' ? 'পণ্য হাতে পেয়ে টাকা পরিশোধ করুন' : 'Pay when you receive',
    },
    {
      id: 'bkash',
      label: t('checkout.bkash'),
      icon: BkashIcon,
      description: 'bKash Payment',
    },
    {
      id: 'nagad',
      label: t('checkout.nagad'),
      icon: NagadIcon,
      description: 'Nagad Payment',
    },
  ];

  return (
    <div className="min-h-[100dvh] bg-white font-sans">
      <div className="animate-fade-in pb-24 lg:pb-10">
        {/* Premium Header — Gold Standard */}
        <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl hover:bg-gray-50 text-black transition-all active:scale-95"
              onClick={() => navigate('/cart')}
            >
              <FaCircleArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-lg font-black tracking-tight text-center flex-1">{t('checkout.title')}</h1>
            <div className="w-10" /> {/* Spacer */}
          </div>
        </header>

        <div className="container mx-auto px-4">
          <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-4 lg:gap-8">
            {/* Left Column - Forms */}
            <div className="lg:col-span-2 space-y-4 lg:space-y-6">
              {/* Delivery Information */}
              <Card>
                <CardContent className="p-4 lg:p-6 -mb-12">
                  <div className="flex items-center gap-2 mb-4 lg:mb-6">
                    <FaMapPin className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-bold">
                      {t('checkout.delivery_info')}
                    </h2>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="fullName">
                        {t('form.full_name')} *
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                        <Input
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                          className="pl-10"
                          placeholder={t('form.full_name')}
                        />
                      </div>
                    </div>

                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="phone">
                        {t('form.phone')} *
                      </Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="pl-10"
                          placeholder="01XXXXXXXXX"
                        />
                      </div>
                    </div>


                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="city">
                        {t('form.city')} *
                      </Label>
                      <Popover open={cityOpen} onOpenChange={setCityOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={cityOpen}
                            className="w-full justify-between font-normal text-left h-10 px-3 py-2 bg-transparent border border-input rounded-md"
                          >
                            {formData.city ? getLocationName(formData.city, currentLang) : "Select City"}
                            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl" align="start">
                          <div className="sticky top-0 bg-white p-2 border-b border-gray-100 z-10">
                            <div className="relative">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                              <Input 
                                placeholder={currentLang === 'bn' ? 'শহর খুঁজুন...' : 'Search city...'}
                                value={citySearch}
                                onChange={(e) => setCitySearch(e.target.value)}
                                className="pl-8 h-9 text-xs border-none bg-gray-50 focus-visible:ring-1 focus-visible:ring-primary/20 rounded-lg"
                              />
                            </div>
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            {BANGLADESH_CITIES.filter(c => 
                              c.nameen?.toLowerCase().includes(citySearch.toLowerCase()) || 
                              c.namebn?.includes(citySearch)
                            ).map((c) => (
                              <div
                                key={c.id}
                                className="focus:bg-primary/5 hover:bg-primary/5 focus:text-primary py-3 px-4 font-medium cursor-pointer text-sm transition-colors"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, city: c.nameen, area: '' }));
                                  setCityOpen(false);
                                  setCitySearch('');
                                }}
                              >
                                {currentLang === 'bn' ? c.namebn : c.nameen}
                              </div>
                            ))}
                            {BANGLADESH_CITIES.filter(c => 
                              c.nameen?.toLowerCase().includes(citySearch.toLowerCase()) || 
                              c.namebn?.includes(citySearch)
                            ).length === 0 && (
                              <div className="py-6 text-center text-xs text-gray-400">
                                {currentLang === 'bn' ? 'কোনো শহর পাওয়া যায়নি' : 'No cities found'}
                              </div>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="area">
                        {currentLang === 'bn' ? 'এলাকা' : 'Area'} *
                      </Label>
                      {formData.city && CITY_AREAS[formData.city] ? (
                    <Popover open={areaOpen} onOpenChange={setAreaOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={areaOpen}
                          className="w-full justify-between font-normal text-left h-10 px-3 py-2 bg-transparent border border-input rounded-md"
                        >
                          {formData.area ? getLocationName(formData.area, currentLang) : (currentLang === 'bn' ? "এলাকা নির্বাচন করুন" : "Select Area")}
                          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl" align="start">
                        <div className="sticky top-0 bg-white p-2 border-b border-gray-100 z-10">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <Input 
                              placeholder={currentLang === 'bn' ? 'এলাকা খুঁজুন...' : 'Search area...'}
                              value={areaSearch}
                              onChange={(e) => setAreaSearch(e.target.value)}
                              className="pl-8 h-9 text-xs border-none bg-gray-50 focus-visible:ring-1 focus-visible:ring-primary/20 rounded-lg"
                            />
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {CITY_AREAS[formData.city].filter(a => 
                            a.nameen?.toLowerCase().includes(areaSearch.toLowerCase()) || 
                            a.namebn?.includes(areaSearch)
                          ).map((a) => (
                            <div
                              key={a.id}
                              className="focus:bg-primary/5 hover:bg-primary/5 focus:text-primary py-3 px-4 font-medium cursor-pointer text-sm transition-colors"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, area: a.nameen }));
                                setAreaOpen(false);
                                setAreaSearch('');
                              }}
                            >
                              {currentLang === 'bn' ? a.namebn : a.nameen}
                            </div>
                          ))}
                          {CITY_AREAS[formData.city].filter(a => 
                            a.nameen?.toLowerCase().includes(areaSearch.toLowerCase()) || 
                            a.namebn?.includes(areaSearch)
                          ).length === 0 && (
                            <div className="py-6 text-center text-xs text-gray-400">
                              {currentLang === 'bn' ? 'কোনো এলাকা পাওয়া যায়নি' : 'No areas found'}
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                      ) : (
                        <Input
                          id="area"
                          name="area"
                          value={formData.area}
                          onChange={handleInputChange}
                          required
                          disabled={!formData.city}
                          placeholder={currentLang === 'bn' ? "যেমন: উত্তরা" : "e.g. Uttara"}
                        />
                      )}
                    </div>

                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="street">
                        {t('form.street')} *
                      </Label>
                      <Input
                        id="street"
                        name="street"
                        value={formData.street}
                        onChange={handleInputChange}
                        required
                        placeholder={currentLang === 'bn' ? 'বাসা নম্বর, রোড নম্বর ইত্যাদি' : 'House no, Road no, etc.'}
                      />
                    </div>


                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="notes">
                        {t('order.notes')}
                      </Label>
                      <Input
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder={currentLang === 'bn' ? 'বিশেষ নির্দেশনা (ঐচ্ছিক)' : 'Special instructions (optional)'}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card>
                <CardContent className="p-4 lg:p-6 -mb-12">
                  <div className="flex items-center gap-2 mb-4 lg:mb-6">
                    <FaRegCreditCard className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-bold">
                      {t('checkout.payment_method')}
                    </h2>
                  </div>

                  <RadioGroup
                    value={paymentMethod}
                    onValueChange={setPaymentMethod}
                    className="flex flex-col gap-0"
                  >
                    {paymentMethods.map((method) => (
                      <label
                        key={method.id}
                        htmlFor={method.id}
                        className={`flex items-center gap-4 px-4 rounded-lg cursor-pointer transition-colors ${paymentMethod === method.id
                          // ? 'bg-primary/5'
                          // : 'border-1 hover:border-gray-200'
                          }`}
                      >
                        <RadioGroupItem value={method.id} id={method.id} className="border-primary" />
                        <method.icon className="h-20 w-24 object-contain" />
                        <div className="flex flex-col flex-1">
                          <span className="text-sm text-black font-bold">
                            {method.label}
                          </span>
                          <span className="text-xs text-gray-500">
                            {method.description}
                          </span>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>

              {/* Coupon Code */}
              <Card>
                <CardContent className="p-4 lg:p-6">
                  <div className="flex items-center gap-2 mb-4 lg:mb-6">
                    <PiTicketFill className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-bold">
                      {currentLang === 'bn' ? 'কুপন কোড' : 'Coupon Code'}
                    </h2>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder={currentLang === 'bn' ? 'একটি কুপন কোড লিখুন' : 'Enter a coupon code'}
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value);
                        if (couponError) setCouponError(null);
                      }}
                      className="flex-1"
                      disabled={!!appliedCoupon}
                    />
                    <Button 
                      type="button"
                      className="bg-primary text-white"
                      onClick={appliedCoupon ? () => { 
                        setAppliedCoupon(null); 
                        setCouponCode(''); 
                        setCouponError(null);
                      } : () => handleApplyCoupon()}
                      disabled={isApplyingCoupon || (!couponCode.trim() && !appliedCoupon)}
                    >
                      {isApplyingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                        appliedCoupon ? (currentLang === 'bn' ? 'সরান' : 'Remove') : (currentLang === 'bn' ? 'প্রয়োগ করুন' : 'Apply')
                      )}
                    </Button>
                  </div>
                  {couponError && <p className="text-sm text-red-500 mt-2">{couponError}</p>}
                  {appliedCoupon && (
                    <p className="text-sm text-green-600 mt-2">
                      {currentLang === 'bn' ? 'কুপন প্রয়োগ করা হয়েছে!' : 'Coupon applied successfully!'}
                    </p>
                  )}

                  {/* Available Coupons Dropdown */}
                  <div className="mt-4 border-t pt-4 -mb-12">
                    <button
                      type="button"
                      onClick={() => setShowCoupons(!showCoupons)}
                      className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-primary transition-colors"
                    >
                      {currentLang === 'bn' ? 'উপলব্ধ কুপনগুলি দেখুন' : 'See available coupons'}
                      <ChevronDown className={`w-4 h-4 transition-transform ${showCoupons ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showCoupons && (
                      <div className="mt-3 space-y-3">
                        {coupons.filter(c => c.is_active).map(coupon => {
                          const subtotal = getTotalPrice();
                          const isEligible = subtotal >= coupon.min_order_amount;
                          const diff = coupon.min_order_amount - subtotal;
                          
                          return (
                            <div key={coupon.id} className="border rounded-lg p-3 flex flex-row gap-3 justify-between items-center bg-gray-50/50">
                              <div>
                                <div className="font-bold text-primary mb-0.5 uppercase">{coupon.code}</div>
                                <div className="text-xs text-gray-600 font-medium">
                                  {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% OFF` : `৳${coupon.discount_value} OFF`}
                                  {coupon.max_discount_amount ? ` (Up to ৳${coupon.max_discount_amount})` : ''}
                                </div>
                                {!isEligible && (
                                  <div className="text-[10px] text-red-500 mt-1 font-medium">
                                    {currentLang === 'bn' 
                                      ? `অফারটি পেতে আরও ৳${diff.toFixed(2)} টাকার শপিং করুন` 
                                      : `Add ৳${diff.toFixed(2)} more to use this coupon`}
                                  </div>
                                )}
                              </div>
                              
                              <Button
                                type="button"
                                variant={isEligible ? "default" : "secondary"}
                                size="sm"
                                disabled={!isEligible || isApplyingCoupon || appliedCoupon?.id === coupon.id}
                                onClick={() => {
                                  setCouponCode(coupon.code);
                                  handleApplyCoupon(coupon.code);
                                }}
                                className={isEligible ? "bg-primary text-white" : "opacity-60 cursor-not-allowed"}
                              >
                                {appliedCoupon?.id === coupon.id 
                                  ? (currentLang === 'bn' ? 'প্রয়োগ করা হয়েছে' : 'Applied') 
                                  : (currentLang === 'bn' ? 'প্রয়োগ করুন' : 'Apply')}
                              </Button>
                            </div>
                          );
                        })}
                        {coupons.filter(c => c.is_active).length === 0 && (
                          <div className="text-sm text-gray-500 text-center py-2">
                            {currentLang === 'bn' ? 'বর্তমানে কোনো কুপন নেই' : 'No coupons available right now'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1 ">
              <Card className="sticky top-24">
                <CardContent className="p-4 lg:p-6 -mb-12">
                  <div className="flex items-center gap-2 mb-4 lg:mb-6">
                    <TfiLayoutListThumbAlt className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-bold">
                      {t('checkout.order_summary')}
                    </h2>
                  </div>

                  {/* Items */}
                  <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                    {items.map((item) => (
                      <div key={item.productId} className="flex justify-between text-sm text-black font-medium">
                        <span className="truncate flex-1">
                          {currentLang === 'bn' ? item.nameBn : item.name} x {item.quantity}
                        </span>
                        <span className="text-black font-medium">
                          ৳{(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <Separator className="my-4" />

                  {/* Totals */}
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-black font-medium flex items-center gap-2">
                        <IoPricetags className="h-4 w-4" />
                        {t('cart.product_cost')}
                      </span>
                      <span className="text-black font-medium">৳{getTotalPrice().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-black font-medium flex items-center gap-2">
                        <FaShippingFast className="h-4 w-4" />
                        {t('cart.delivery_fee')}
                      </span>
                      <span>
                        {getDeliveryFee() === 0 ? (
                          <span className="text-green-600 font-medium">
                            {currentLang === 'bn' ? 'ফ্রি' : 'Free'}
                          </span>
                        ) : (
                          <span className="text-black font-medium">৳{getDeliveryFee().toFixed(2)}</span>
                        )}
                      </span>
                    </div>
                    <div className={`text-sm font-medium p-2 rounded flex items-center justify-center ${
                      getDeliveryFee() === 0 
                        ? 'text-green-600 bg-green-50' 
                        : 'text-gray-500 bg-gray-100'
                    }`}>
                      {currentLang === 'bn'
                       ? `🎉 ৳${deliveryConfig.freeDeliveryThreshold}+ অর্ডারে ফ্রি ডেলিভারি!`
                       : `🎉 Free delivery on orders over ৳${deliveryConfig.freeDeliveryThreshold}!`}
                    </div>
                    {appliedCoupon && (
                      <div className="flex justify-between rounded">
                        <span className="flex items-center gap-2 text-green-600 font-medium">
                          <Tag className="h-4 w-4" />
                          {t('cart.discount')} ({appliedCoupon.code})
                        </span>
                        <span className="text-green-600 font-medium">-৳{calculateDiscount().toFixed(2)}</span>
                      </div>
                    )}
                    <Separator />
                    <div className="flex justify-between text-lg font-bold">
                      <span>{t('cart.total')}</span>
                      <span className="text-primary">
                        ৳{finalTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full mt-6"
                    disabled={isLoading}
                    onMouseEnter={warmConnection}
                    onTouchStart={warmConnection}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {currentLang === 'bn' ? 'প্রসেসিং...' : 'Processing...'}
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        {t('btn.place_order')}
                      </>
                    )}
                  </Button>

                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
      <p className="text-xs text-center text-gray-700 mt-8 whitespace-nowrap overflow-x-auto px-4">
        {currentLang === 'bn'
          ? 'অর্ডার করার মাধ্যমে আপনি আমাদের শর্তাবলী মেনে নিচ্ছেন'
          : 'By placing order, you agree to our Terms & Conditions'}
      </p>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={handleSuccessClose}>
        <DialogContent className="sm:max-w-md animate-fade-in border-none shadow-2xl p-0 overflow-hidden bg-white rounded-2xl">
          <style>
            {`
              @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');
              .thank-you-text {
                font-family: 'Dancing Script', cursive;
                color: #4a3629;
                font-size: 5rem;
                line-height: 1;
                text-shadow: 
                  -3px -3px 0 #f5ebe6, 
                  3px -3px 0 #f5ebe6, 
                  -3px 3px 0 #f5ebe6, 
                  3px 3px 0 #f5ebe6,
                  0px 0px 8px rgba(245, 235, 230, 0.8);
                position: relative;
                z-index: 10;
                padding-top: 10px;
              }
            `}
          </style>
          
          <div className="px-6 pt-10 pb-2 flex flex-col items-center justify-center relative overflow-hidden bg-white">
             {/* Logo removed per request */}

             <div className="relative mt-2 mb-2 flex justify-center w-full">
                <svg width="0" height="0" className="absolute">
                  <defs>
                    <linearGradient id="chocoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#A16E5A" />
                      <stop offset="50%" stopColor="#5D4037" />
                      <stop offset="100%" stopColor="#3E2723" />
                    </linearGradient>
                  </defs>
                </svg>
                <Heart className="absolute -top-5 left-[65%] -translate-x-1/2 w-14 h-14 rotate-[15deg] opacity-95 z-0 text-transparent drop-shadow-sm" strokeWidth={0} fill="url(#chocoGradient)" />
                <h1 className="thank-you-text text-center w-full">thank you</h1>
             </div>

             <h2 className="text-[#3b3531] text-lg sm:text-xl font-bold tracking-[0.15em] uppercase mt-1 mb-1 text-center">
               {currentLang === 'bn' ? 'অর্ডারের জন্য ধন্যবাদ' : 'For Your Purchase'}
             </h2>

             <div className="w-4/5 h-[1.5px] bg-[#3b3531] my-2.5" />

             <p className="text-center text-[#555] text-sm sm:text-base leading-snug px-2 font-medium">
               {currentLang === 'bn' 
                 ? 'আপনার অর্ডারের জন্য অনেক ধন্যবাদ। আমাদের ছোট্ট উদ্যোগে আপনার সমর্থনের জন্য আমরা সত্যিই কৃতজ্ঞ!' 
                 : 'Thank you so much dear customer for your order. We really appreciate you supporting our small business!'}
             </p>

             {/* Order ID removed per request */}
          </div>

          <div className="px-6 pb-6 pt-2 bg-white">
            <Button 
              onClick={handleSuccessClose} 
              className="w-full h-12 rounded-xl text-base font-bold shadow-md hover:shadow-lg transition-all bg-[#4a3629] hover:bg-[#3b2b21] text-white tracking-wide"
            >
              {t('nav.track_order')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}
